import { PlannedTradeDirection } from '../pages/TradingPlan/types'

export interface RiskMetrics {
  potentialProfit: number | null
  potentialLoss: number | null
  riskRewardRatio: number | null
  riskPercent: number | null
  breakevenWinPercent: number | null
}

/* ----------  BASIC HELPERS  ---------- */

/**
 * Checks if all numeric inputs are valid for calculation.
 */
const isValidInputs = (
  entryPrice: number,
  stopLoss: number,
  takeProfit: number,
  positionSize: number,
  contractSize: number,
  // balance: number | null | undefined,
  direction: PlannedTradeDirection | null,
): boolean => {
  return (
    !Number.isNaN(entryPrice) &&
    !Number.isNaN(stopLoss) &&
    !Number.isNaN(takeProfit) &&
    !Number.isNaN(positionSize) &&
    !Number.isNaN(contractSize) &&
    contractSize > 0 &&
    positionSize > 0 &&
    // typeof balance === 'number' &&
    // balance > 0 &&
    (direction === 'long' || direction === 'short')
  )
}

/**
 * Returns raw (unscaled) profit and loss in price points.
 */
const calculatePriceDiffs = (
  entryPrice: number,
  stopLoss: number,
  takeProfit: number,
  direction: PlannedTradeDirection,
): { priceProfit: number; priceLoss: number } => {
  if (direction === 'long') {
    return {
      priceProfit: takeProfit - entryPrice,
      priceLoss: entryPrice - stopLoss,
    }
  }
  // short
  return {
    priceProfit: entryPrice - takeProfit,
    priceLoss: stopLoss - entryPrice,
  }
}

/**
 * Converts price differences to monetary potential.
 */
const calculatePotentials = (
  priceProfit: number,
  priceLoss: number,
  positionSize: number,
  contractSize: number,
): { potentialProfit: number; potentialLoss: number } => {
  const normalizedContractSize =
    Number.isFinite(contractSize) && contractSize > 0 ? contractSize : 1
  const multiplier = positionSize * normalizedContractSize

  return {
    potentialProfit: Math.max(priceProfit, 0) * multiplier,
    potentialLoss: Math.max(priceLoss, 0) * multiplier,
  }
}

/**
 * Calculates risk:reward ratio (shown as RISK : REWARD, e.g. 2:1).
 */
export const calculateRiskRewardRatio = (
  potentialProfit: number,
  potentialLoss: number,
): number | null => {
  if (potentialProfit <= 0 || potentialLoss <= 0) return null
  return parseFloat((potentialLoss / potentialProfit).toFixed(2))
}

/**
 * Calculates risk percentage of account balance.
 */
export const calculateRiskPercent = (
  potentialLoss: number,
  balance: number | null | undefined,
): number | null => {
  if (!balance || potentialLoss <= 0) return null
  return (potentialLoss / balance) * 100
}

/**
 * Calculates breakeven win rate percentage.
 * Formula: (Risk / (Risk + Reward)) * 100
 */
const calculateBreakevenWinPercent = (riskRewardRatio: number | null): number | null => {
  if (typeof riskRewardRatio !== 'number' || riskRewardRatio <= 0) return null
  return parseFloat(((1 / (1 + riskRewardRatio)) * 100).toFixed(2))
}

/* ----------  MAIN FUNCTION  ---------- */

export const calculateRiskMetrics = (
  entryPrice: number,
  stopLoss: number,
  takeProfit: number,
  positionSize: number,
  contractSize: number,
  balance: number | null | undefined,
  direction: PlannedTradeDirection | null,
): RiskMetrics => {
  if (!isValidInputs(entryPrice, stopLoss, takeProfit, positionSize, contractSize, direction)) {
    return {
      potentialProfit: null,
      potentialLoss: null,
      riskRewardRatio: null,
      riskPercent: null,
      breakevenWinPercent: null,
    }
  }

  const normalizedContractSize =
    Number.isFinite(contractSize) && contractSize > 0 ? contractSize : 1

  const { priceProfit, priceLoss } = calculatePriceDiffs(
    entryPrice,
    stopLoss,
    takeProfit,
    direction as PlannedTradeDirection,
  )
  const { potentialProfit, potentialLoss } = calculatePotentials(
    priceProfit,
    priceLoss,
    positionSize,
    normalizedContractSize,
  )

  const riskRewardRatio = calculateRiskRewardRatio(potentialProfit, potentialLoss)
  const riskPercent = calculateRiskPercent(potentialLoss, balance)
  const breakevenWinPercent = calculateBreakevenWinPercent(riskRewardRatio)

  return {
    potentialProfit,
    potentialLoss,
    riskRewardRatio,
    riskPercent,
    breakevenWinPercent,
  }
}
